// src/app/core/logging/logger.service.ts
import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ConfigService } from '../config/config.service';
import { environment } from '../../../environments/environment';

type LogLevel = 'error' | 'warn' | 'info';

const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
  error: 0,
  warn:  1,
  info:  2,
};

/** Payload enviado al servidor — NUNCA incluir stack traces, tokens ni contraseñas */
interface LogPayload {
  level:       LogLevel;
  context:     string;
  message:     string;
  timestamp:   string;
  appVersion:  string;
  env:         string;
  /** Incluido cuando hay contexto extra no sensible */
  extra?:      Record<string, unknown>;
}

@Injectable({ providedIn: 'root' })
export class LoggerService {
  private readonly http   = inject(HttpClient);
  private readonly config = inject(ConfigService);

  /**
   * Registra un error.
   * En development: console.error.
   * En production: POST al endpoint configurado sin stack trace.
   */
  error(context: string, error: unknown, extra?: Record<string, unknown>): void {
    this.log('error', context, this.extractMessage(error), extra);
  }

  /**
   * Registra una advertencia.
   * En development: console.warn.
   * En production: POST si level <= 'warn'.
   */
  warn(context: string, message: string): void {
    this.log('warn', context, message);
  }

  /**
   * Registra información.
   * En development: console.error (suprimido por ESLint no-console).
   * En production: POST si level <= 'info'.
   */
  info(context: string, message: string): void {
    this.log('info', context, message);
  }

  private log(
    level: LogLevel,
    context: string,
    message: string,
    extra?: Record<string, unknown>
  ): void {
    let loggingCfg;
    try {
      loggingCfg = this.config.get('logging');
    } catch {
      // ConfigService todavía no cargó (error ocurrido durante el bootstrap,
      // antes de que APP_INITIALIZER resuelva). No hay dónde enviar el log
      // todavía — usar console.error como último recurso en vez de lanzar
      // un segundo error que enmascare la causa original.
      console.error(`[BEL:${context}]`, message, extra ?? '');
      return;
    }

    // Si logging está deshabilitado: no-op completo
    if (!loggingCfg.enabled) return;

    // Verificar si este nivel debe registrarse
    const configuredLevel = loggingCfg.level as LogLevel;
    if (LOG_LEVEL_PRIORITY[level] > LOG_LEVEL_PRIORITY[configuredLevel]) {
      return;
    }

    // "Desplegado" = production o staging — ambos deben POSTear al backend
    // real para que el equipo pueda ver los errores. Solo development (ambos
    // flags en false) usa la consola. Antes esto miraba únicamente
    // "environment.production", así que el build de staging nunca enviaba
    // nada a /api/logs — cualquier error se perdía en la consola del
    // navegador sin que nadie lo notara.
    const isDeployed = environment.production || environment.staging;

    if (!isDeployed) {
      // En desarrollo: mostrar en consola para facilitar el debug
      if (level === 'error') {
        console.error(`[BEL:${context}]`, message, extra ?? '');
      } else if (level === 'warn') {
        console.warn(`[BEL:${context}]`, message);
      }
    } else {
      // En producción/staging: POST al endpoint
      // NUNCA incluir stack traces, tokens, contraseñas ni clientId
      const payload: LogPayload = {
        level,
        context:    this.sanitize(context),
        message:    this.sanitize(message),
        timestamp:  new Date().toISOString(),
        appVersion: environment.appVersion,
        env:        environment.production ? 'production' : 'staging',
      };

      if (extra) {
        payload.extra = this.sanitizeExtra(extra);
      }

      // Fire-and-forget — los errores de logging no deben afectar la UX
      this.http.post(loggingCfg.endpoint, payload).subscribe({
        error: () => {
          // Silenciar errores del endpoint de logging para evitar loops
        }
      });
    }
  }

  /** Extrae un mensaje legible de cualquier tipo de error */
  private extractMessage(error: unknown): string {
    if (error instanceof Error) {
      // Solo el mensaje, no el stack trace
      return error.message;
    }
    if (typeof error === 'string') {
      return error;
    }
    return 'Error desconocido';
  }

  /** Elimina caracteres potencialmente problemáticos de strings de log */
  private sanitize(text: string): string {
    // Limitar longitud para evitar payloads excesivos
    return String(text).slice(0, 500);
  }

  /** Sanitiza el objeto extra eliminando campos sensibles conocidos */
  private sanitizeExtra(extra: Record<string, unknown>): Record<string, unknown> {
    // Todas las claves en minúsculas — se compara contra key.toLowerCase().
    // 'userId', 'clientId' y 'base64EncodedAuthenticationKey' en su forma
    // original (camelCase) NUNCA coincidían aquí, dejando pasar esos campos
    // sensibles sin filtrar hacia el endpoint de logging en producción.
    const SENSITIVE_KEYS = new Set([
      'token', 'password', 'auth', 'authorization',
      'base64encodedauthenticationkey', 'clientid', 'userid',
    ]);
    return Object.fromEntries(
      Object.entries(extra)
        .filter(([key]) => !SENSITIVE_KEYS.has(key.toLowerCase()))
        .map(([key, val]) => {
          if (typeof val !== 'string') return [key, val];
          // 'url' (req.url / router.url) es una excepción a "solo se filtra
          // por nombre de clave": las rutas del self-service embeben el
          // identificador en el propio valor (/clients/482/accounts,
          // /savingsaccounts/5), no como una clave 'clientId' separada, así
          // que el filtro de arriba nunca lo detecta. Enmascarar los
          // segmentos numéricos de la ruta antes de loggear.
          const value = key.toLowerCase() === 'url' ? this.maskUrlIds(val) : val;
          return [key, this.sanitize(value)];
        })
    );
  }

  /** Reemplaza segmentos numéricos de una ruta (IDs de cliente, cuenta, préstamo, etc.) por ':id' */
  private maskUrlIds(url: string): string {
    return url.replace(/\/\d+(?=\/|$|\?)/g, '/:id');
  }
}
